// apps/web/src/features/crm/leads/activity/components/LeadEpisodeFeed.tsx
//
// Hero feed tab of the lead episode page (#1809, #1798 §6.1), re-skinned
// into the `SectionCard` rhythm (#2315, reference `lead-parts.jsx`
// TimelineFeed). Composes: a capture toolbar (note / file / call / message
// drawers, manager-only) + the filters toggle in the SectionCard `action`
// slot, a coarse category tab row (`LeadFeedTabs`) as the first body child,
// the upcoming-tasks ("scheduled") strip, a collapsed-by-default free-text
// search, and the activity feed rendered through the shared
// `JournalTimeline`. Capture drawers invalidate the feed query on success,
// so the timeline refreshes without manual coordination here.
//
// FOUNDER DECISION (2026-07-10): the free-text search survives the re-skin.
// The old 5-option category `Select` is gone — the three `LeadFeedTabs`
// supersede coarse category grouping — but the collapsible search stays,
// composing feature-side with the active tab (item shown when it matches
// BOTH).
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LeadCommunicationKind, type LeadActivityFeedItem } from '@magimp/shared';
import { useLeadActivityFeed } from '@magimp/shared-hooks';
import {
  Button,
  EmptyState,
  ErrorState,
  FilterPanel,
  FilterToggleButton,
  FormRow,
  Input,
  JournalTimeline,
  LoadingState,
  SectionCard,
  ToolbarActions,
  useFilterDisclosure,
} from '../../../../../ui/index.js';
import { errorMessage } from '../../../../../api/errors.js';
import {
  buildLeadFeedEntries,
  leadFeedCategory,
  leadFeedSearchText,
  type LeadFeedFallbacks,
} from '../leadFeedEntries.js';
import { LeadUpcomingTasksStrip } from './LeadUpcomingTasksStrip.js';
import { LeadNoteDrawer } from './LeadNoteDrawer.js';
import { LeadFileDrawer } from './LeadFileDrawer.js';
import { LeadCommunicationDrawer } from './LeadCommunicationDrawer.js';
import { LeadFeedTabs, type LeadFeedTab } from './LeadFeedTabs.js';

export interface LeadEpisodeFeedProps {
  leadId: string;
  canManage: boolean;
}

type OpenDrawer = null | 'note' | 'file' | 'call' | 'message';

export function LeadEpisodeFeed({ leadId, canManage }: LeadEpisodeFeedProps) {
  const { t } = useTranslation(['leads', 'common']);
  const [openDrawer, setOpenDrawer] = useState<OpenDrawer>(null);
  const [tab, setTab] = useState<LeadFeedTab>('all');
  const [search, setSearch] = useState('');
  const { open, toggle, panelId } = useFilterDisclosure();

  // #2317 D-8: this card is the surface that shows task cancel/reopen audit
  // rows, so it opts into the #2319 rolling-safe header (sends
  // `X-Magimp-Lead-Activity-V2` + reads a distinct cache slot). Other feed
  // callers stay on the default (byte-compatible) read.
  const feedQ = useLeadActivityFeed(leadId, { includeTaskCancelReopen: true });

  const fallbacks: LeadFeedFallbacks = useMemo(
    () => ({
      actorSystem: t('leads:activity.feed.actorSystem'),
      commentDeleted: t('leads:activity.feed.commentDeleted'),
      fileDeleted: t('leads:activity.feed.fileDeleted'),
      communicationDeleted: t('leads:activity.communications.communicationDeleted'),
      transcriptPlaceholder: t('leads:activity.communications.transcriptPlaceholder'),
    }),
    [t],
  );

  // WHY (D-3, founder decision): the tabs are the always-visible primary
  // filter, not an "active filter" — the badge on the Filters toggle
  // reflects the search only.
  const filtersActiveCount = search.trim().length > 0 ? 1 : 0;

  const action = (
    <ToolbarActions>
      {canManage && (
        <>
          <Button
            kind="secondary"
            size="sm"
            icon="pencil-line"
            onClick={() => setOpenDrawer('note')}
            data-testid="lead-feed-capture-note"
          >
            {t('leads:episode.capture.note')}
          </Button>
          <Button
            kind="secondary"
            size="sm"
            icon="upload"
            onClick={() => setOpenDrawer('file')}
            data-testid="lead-feed-capture-file"
          >
            {t('leads:episode.capture.file')}
          </Button>
          <Button
            kind="secondary"
            size="sm"
            icon="phone"
            onClick={() => setOpenDrawer('call')}
            data-testid="lead-feed-capture-call"
          >
            {t('leads:episode.capture.call')}
          </Button>
          <Button
            kind="secondary"
            size="sm"
            icon="message-square"
            onClick={() => setOpenDrawer('message')}
            data-testid="lead-feed-capture-message"
          >
            {t('leads:episode.capture.message')}
          </Button>
        </>
      )}
      <FilterToggleButton
        open={open}
        activeCount={filtersActiveCount}
        label={t('leads:episode.filters.toggle')}
        aria-controls={panelId}
        onClick={toggle}
        data-testid="lead-feed-filters-toggle"
      />
    </ToolbarActions>
  );

  return (
    <div data-testid="lead-episode-feed">
      <SectionCard title={t('leads:episode.tabs.feed')} action={action}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
          <LeadFeedTabs value={tab} onChange={setTab} />

          <FilterPanel
            open={open}
            id={panelId}
            style={{ padding: 0, border: 'none', background: 'transparent' }}
          >
            <FormRow label={t('leads:episode.filters.searchLabel')}>
              <Input
                icon="search"
                value={search}
                onChange={setSearch}
                placeholder={t('leads:episode.filters.searchPlaceholder')}
                ariaLabel={t('leads:episode.filters.searchLabel')}
              />
            </FormRow>
          </FilterPanel>

          <LeadUpcomingTasksStrip leadId={leadId} />

          <FeedBody feedQ={feedQ} tab={tab} search={search} fallbacks={fallbacks} />
        </div>
      </SectionCard>

      {openDrawer === 'note' && (
        <LeadNoteDrawer leadId={leadId} onClose={() => setOpenDrawer(null)} />
      )}
      {openDrawer === 'file' && (
        <LeadFileDrawer leadId={leadId} onClose={() => setOpenDrawer(null)} />
      )}
      {openDrawer === 'call' && (
        <LeadCommunicationDrawer
          leadId={leadId}
          kind={LeadCommunicationKind.CALL}
          onClose={() => setOpenDrawer(null)}
        />
      )}
      {openDrawer === 'message' && (
        <LeadCommunicationDrawer
          leadId={leadId}
          kind={LeadCommunicationKind.MESSAGE}
          onClose={() => setOpenDrawer(null)}
        />
      )}
    </div>
  );
}

/** Whether a feed item's coarse category belongs to the active tab. */
function matchesTab(item: LeadActivityFeedItem, tab: LeadFeedTab): boolean {
  if (tab === 'all') return true;
  const isSystem = leadFeedCategory(item.kind) === 'other';
  return tab === 'system' ? isSystem : !isSystem;
}

function FeedBody({
  feedQ,
  tab,
  search,
  fallbacks,
}: {
  feedQ: ReturnType<typeof useLeadActivityFeed>;
  tab: LeadFeedTab;
  search: string;
  fallbacks: LeadFeedFallbacks;
}) {
  const { t } = useTranslation(['leads', 'common']);

  if (feedQ.isPending) return <LoadingState />;
  if (feedQ.error) {
    return (
      <ErrorState
        title={t('common:states.loadError')}
        text={errorMessage(feedQ.error)}
        onRetry={() => void feedQ.refetch()}
      />
    );
  }

  const items: LeadActivityFeedItem[] = feedQ.data.pages.flatMap((p) => p.items);
  if (items.length === 0) {
    return <EmptyState icon="package" title={t('leads:activity.feed.empty')} />;
  }

  // WHY (#1809, tabs added #2315): no backend filter param exists for the
  // feed, so the tab + search apply CLIENT-SIDE over the already-loaded
  // pages only — an item is shown when it matches BOTH.
  const needle = search.trim().toLowerCase();
  const filtered = items.filter((item) => {
    if (!matchesTab(item, tab)) return false;
    if (needle !== '' && !leadFeedSearchText(item, t, fallbacks).includes(needle)) return false;
    return true;
  });

  if (filtered.length === 0) {
    return <EmptyState icon="search" title={t('leads:activity.feed.empty')} />;
  }

  const entries = buildLeadFeedEntries(filtered, t, fallbacks);
  return (
    <div data-testid="lead-episode-feed-timeline">
      <JournalTimeline entries={entries} />
      {feedQ.hasNextPage && (
        <Button
          size="sm"
          kind="ghost"
          onClick={() => void feedQ.fetchNextPage()}
          disabled={feedQ.isFetchingNextPage}
          data-testid="lead-activity-load-more"
          style={{ marginTop: 12 }}
        >
          {feedQ.isFetchingNextPage ? t('common:states.loading') : t('leads:actions.loadMore')}
        </Button>
      )}
    </div>
  );
}
